import React from 'react'
import useInsights from '../../../hooks/algolia/useInsights'
import SpecializationChips from './SpecializationChips'
import { NearbyCentersLabel } from './NearbyCentersLabel'
import SegmentCardTracker from './SegmentCardTracker'
import { CenterCardDescription } from './CenterCardDescription'
import { getCoOccurringDisorderLabel } from '@/domain/primaryFocus/useCases/getCoOccurringDisorderLabel'
import StandardProfileFeatureImage from './StandardProfileFeatureImage'
import { shouldShowDistanceLabel } from '@/domain/feed/useCases/shouldShowDistanceLabel'
import { getCenterProfileSlug, isSamhsa } from '../../../domain/center'
import CenterBadgesAndRatingSection from './CenterBadgesAndRatingSection'
import CenterCardHeader from './CenterCardHeader'
import {
  computeAllReviewsCount,
  computeAllReviewsWeightedAverage
} from '../../../domain/centerReviews/centerReviews'
import FavoriteCenterHeartButtonCenterFeed from '@/components/FavoriteCenterHeartButtonCenterFeed/FavoriteCenterHeartButtonCenterFeed'
import { isFavorite } from '@/domain/favoriteCenters/updateFavoriteStorage'
import { parseCenterId } from '@/domain/center/useCases/parseCenterId'

const Standard = ({
  activeFacets,
  cardIndex,
  highlighted_specializations,
  locationLabel,
  new_specializations,
  rankingInfo,
  showDistanceLabel,
  slug,
  title,
  providerType,
  shouldDefaultRegionalize = false,
  zoneId,
  __queryID,
  currentRefinements,
  center,
  refinementLocation,
  locations,
  hasCertifications,
  pageTitle,
  department,
  termFeedMode,
  pageType
}) => {
  highlighted_specializations = highlighted_specializations || []

  const { userID, recordAlgoliaClick } = useInsights()

  const handleClick = () => {
    recordAlgoliaClick({
      objectID: slug,
      queryID: __queryID,
      userID,
      event: 'ListingPageClick',
      position: cardIndex + 1,
      referrer: window.location.pathname
    })

    window.localStorage.setItem('queryID', __queryID)
  }

  const displayDistanceLabel = shouldShowDistanceLabel({
    providerType,
    rankingInfo,
    showDistanceLabel,
    centerLocations: locations,
    refinementLocation
  })

  const allReviewsCount = computeAllReviewsCount(center)
  const allReviewsWeightedAverage = computeAllReviewsWeightedAverage(center)

  const coOccurringDisorder = getCoOccurringDisorderLabel(center)

  return (
    <SegmentCardTracker
      center={{
        title,
        slug,
        ...center
      }}
      position={cardIndex + 1}
      zone={zoneId}
      refinements={currentRefinements}
      refinementLocation={refinementLocation}
    >
      <div
        className="hit algolia-hit max-[600px]:w-full mt-6 rounded-2xl border border-gray-300 transition-shadow duration-300 hover:shadow-card sm:mt-8"
        data-insights-object-id={slug}
        data-insights-position={cardIndex}
        onClick={handleClick}
      >
        <div
          className={`flex flex-wrap overflow-hidden rounded-[0.95rem] bg-white md:flex-nowrap md:overflow-visible md:rounded-t-[0.95rem] ${
            !rankingInfo ? 'md:min-h-[240px]' : 'md:min-h-[270px]'
          }`}
        >
          <a
            className="algolia-hit-link max-[767px]:flex-col max-[767px]:flex-wrap max-[767px]:p-0 min-[768px]:flex-nowrap relative flex w-full"
            id="standardCard"
            target="_blank"
            href={getCenterProfileSlug(center)}
            rel={center.site === 'lux' ? 'noreferrer' : 'noreferrer nofollow'}
          >
            <div className="relative w-full md:w-[308px]">
              <div className="absolute right-1 top-1 md:right-1 md:top-1">
                <FavoriteCenterHeartButtonCenterFeed
                  isFavorite={isFavorite(parseCenterId({ pageType, center }))}
                  centerId={parseCenterId({ pageType, center })}
                  centerSlug={slug}
                  pageType={pageType}
                />
              </div>
              <StandardProfileFeatureImage center={center} />
            </div>
            <div className="flex flex-col gap-4 p-4 md:w-[calc(100%-308px)] md:justify-center md:px-6">
              <div className="flex flex-col gap-2">
                <CenterCardHeader
                  title={title}
                  providerType={providerType}
                  isAd={false}
                  hasCertifications={hasCertifications}
                  isPremiumCenter={false}
                  center={center}
                />

                <div className="flex flex-col gap-3">
                  {displayDistanceLabel && (
                    <NearbyCentersLabel
                      locationLabel={locationLabel}
                      rankingInfo={rankingInfo}
                      pageTitle={pageTitle}
                      refinementLocation={refinementLocation}
                    />
                  )}
                  <CenterBadgesAndRatingSection
                    center={center}
                    allReviewsCount={allReviewsCount}
                    allReviewsWeightedAverage={allReviewsWeightedAverage}
                    currentRefinements={currentRefinements}
                  />
                </div>
              </div>

              <SpecializationChips
                new_specializations={new_specializations}
                highlighted_specializations={
                  activeFacets || highlighted_specializations || []
                }
                primaryFocus={center?.primaryFocus}
                coOccurringDisorder={coOccurringDisorder}
                isPremium={false}
                termFeedMode={termFeedMode}
                department={department}
              />

              <CenterCardDescription
                center={center}
                activeFacets={activeFacets}
                currentRefinements={currentRefinements}
                shouldDefaultRegionalize={shouldDefaultRegionalize}
                slug={slug}
              />
            </div>
          </a>
        </div>
      </div>
    </SegmentCardTracker>
  )
}

export default Standard
